export type ActiveRowMove = 'next' | 'previous' | 'first' | 'last'

export interface ActiveRowMatch {
	move: ActiveRowMove
	/** Shift+Arrow: extend the selection to the new row instead of only moving the cursor. */
	extendSelection: boolean
}

// WASD is retired from row navigation entirely (not just S's collision with the Settings starter): N/P/W/A/S/D
// all stay reserved for context/focus shifting and sequence starters, a row move keeps only vim's J/K plus
// the arrow keys, so no letter here can ever again double as something else's starter or binding.
export const NEXT_LETTERS = new Set(['j'])
export const PREVIOUS_LETTERS = new Set(['k'])

/**
 * Which way to move the table's active row, if any: vim-style J/K/H/L, the arrow keys, and Ctrl+U/Ctrl+D
 * (or Ctrl+H/Ctrl+L, where those carry no other job) to jump to the first or last row. Pure, so hover and
 * keyboard moves share one answer for "what did this key ask for." Shift only extends the selection on the
 * arrow keys, since a letter held with Shift stays reserved (there is no Shift+J etc.) and a modified jump
 * (Shift+Ctrl+U) stays undefined.
 */
export function matchActiveRowKey(event: KeyboardEvent): ActiveRowMatch | null {
	if (event.altKey) {
		return null
	}
	const mod = event.metaKey || event.ctrlKey
	const key = event.key.toLowerCase()

	if (mod) {
		if (event.shiftKey) {
			return null
		}
		if (key === 'u' || key === 'h') {
			return { move: 'first', extendSelection: false }
		}
		if (key === 'd' || key === 'l') {
			return { move: 'last', extendSelection: false }
		}
		return null
	}

	if (event.key === 'ArrowDown') {
		return { move: 'next', extendSelection: event.shiftKey }
	}
	if (event.key === 'ArrowUp') {
		return { move: 'previous', extendSelection: event.shiftKey }
	}
	if (event.shiftKey) {
		return null
	}
	if (NEXT_LETTERS.has(key)) {
		return { move: 'next', extendSelection: false }
	}
	if (PREVIOUS_LETTERS.has(key)) {
		return { move: 'previous', extendSelection: false }
	}
	return null
}
